/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { Link } from "react-router-dom";
import { FlexContainer, Message, Tag, Text, theme } from "@nordcloud/gnui";
import { inflect } from "~/tools/string";
import type { ActionWithMissingData } from "../types";
import { buildResourceUpdateStatusLink } from "../utils";

type Props = {
  resourceId: string;
  actionsWithMissingData: ActionWithMissingData[];
  title: string;
  subtitle?: string;
};

type RelatedActionLink = {
  key: string;
  eventName: string;
  eventId: string;
  to: string;
};

export function MissingLogsMessage({
  resourceId,
  actionsWithMissingData,
  title,
  subtitle = "We detected issues in the event logs for this resource.",
}: Props) {
  const relatedActions = useMemo<RelatedActionLink[]>(
    () =>
      actionsWithMissingData.map((action) => ({
        key: `${action.eventId}:${action.eventActionId}:${action.errCode}`,
        eventName: action.eventName,
        eventId: action.eventId,
        to: buildResourceUpdateStatusLink({
          resourceId,
          eventId: action.eventId,
          eventActionId: action.eventActionId,
          includeReportView: false,
        }),
      })),
    [actionsWithMissingData, resourceId]
  );

  const eventCountLabel = `${relatedActions.length} ${inflect(
    "Event",
    "Events"
  )(relatedActions.length)}`;

  const expandedContent = (
    <div>
      <Text
        mb={0}
        size="sm"
        weight="medium"
        color={theme.color.text.text02}
        css={{
          padding: `${theme.spacing.spacing04} ${theme.spacing.spacing04} ${theme.spacing.spacing02}`,
        }}
      >
        Related Event
      </Text>
      {relatedActions.map((action) => (
        <div
          key={action.key}
          css={{ borderTop: `1px solid ${theme.color.border.border01}` }}
        >
          <Link
            to={action.to}
            css={{
              display: "block",
              padding: `${theme.spacing.spacing03} ${theme.spacing.spacing04}`,
              overflowWrap: "anywhere",
            }}
          >
            {action.eventName}
          </Link>
        </div>
      ))}
    </div>
  );

  return (
    <Message
      status="danger"
      image="danger"
      expandable
      expandedContent={expandedContent}
      css={{
        "& button[aria-controls]": {
          color: theme.color.text.text01,
        },
      }}
    >
      <FlexContainer alignItems="center" css={{ width: "100%", minWidth: 0 }}>
        <div>
          <Text mb={0} color={theme.color.text.error}>
            {title}
          </Text>
          <Text mb={0} size="md" color={theme.color.text.text02}>
            {subtitle}
          </Text>
        </div>
        <div css={{ marginLeft: "auto", "& > div": { pointerEvents: "none" } }}>
          <Tag text={eventCountLabel} color="danger" />
        </div>
      </FlexContainer>
    </Message>
  );
}
